import { useMutation, useQueryClient } from "@tanstack/react-query";
import { jobKeys } from "../job/job.options";
import type { ServiceResult } from "../job/job.types";
import { ticketKeys } from "../ticket/ticket.options";
import { formDefinitionKeys } from "./form-definition.options";
import {
	type FormDefinitionRequestOptions,
	formDefinitionService,
} from "./form-definition.service";
import type {
	CreateFormDefinitionInfo,
	EditFormDefinitionInfo,
	EditFormLayoutInfo,
	ScopeFields,
} from "./form-definition-core.types";

async function invalidateFormDefinitionRelated(
	queryClient: ReturnType<typeof useQueryClient>,
) {
	await queryClient.invalidateQueries({ queryKey: formDefinitionKeys.all });
	await queryClient.invalidateQueries({
		queryKey: [...jobKeys.jobs(), "job-types"],
	});
	await queryClient.invalidateQueries({
		queryKey: [...ticketKeys.tickets(), "ticket-types"],
	});
}

/**
 * POST /form-definitions
 */
export function useCreateFormDefinition() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			body,
			requestOptions,
		}: {
			body: CreateFormDefinitionInfo;
			requestOptions?: FormDefinitionRequestOptions;
		}) => formDefinitionService.createFormDefinition(body, requestOptions),
		onSuccess: async (_result: ServiceResult) => {
			await invalidateFormDefinitionRelated(queryClient);
		},
	});
}

/**
 * PUT /form-definitions/{id}
 */
export function useUpdateFormDefinition() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			id,
			body,
			requestOptions,
		}: {
			id: string;
			body: EditFormDefinitionInfo;
			requestOptions?: FormDefinitionRequestOptions;
		}) => formDefinitionService.updateFormDefinition(id, body, requestOptions),
		onSuccess: async (_result, { id }) => {
			await invalidateFormDefinitionRelated(queryClient);
			await queryClient.invalidateQueries({
				queryKey: formDefinitionKeys.detail(id),
			});
		},
	});
}

/**
 * POST /form-definitions/{id}/version
 */
export function useCreateFormDefinitionVersion() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: ScopeFields }) =>
			formDefinitionService.createFormDefinitionVersion(id, body),
		onSuccess: async (_result, { id }) => {
			await invalidateFormDefinitionRelated(queryClient);
			await queryClient.invalidateQueries({
				queryKey: formDefinitionKeys.detail(id),
			});
		},
	});
}

/**
 * PUT /form-definitions/{id}/layout
 */
export function useUpdateFormDefinitionLayout() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: EditFormLayoutInfo }) =>
			formDefinitionService.updateFormDefinitionLayout(id, body),
		onSuccess: async (_result, { id }) => {
			await invalidateFormDefinitionRelated(queryClient);
			await queryClient.invalidateQueries({
				queryKey: formDefinitionKeys.detail(id),
			});
		},
	});
}

/**
 * PUT /form-definitions/{id}/activate
 */
export function useActivateFormDefinition() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			id,
			requestOptions,
		}: {
			id: string;
			requestOptions?: FormDefinitionRequestOptions;
		}) => formDefinitionService.activateFormDefinition(id, requestOptions),
		onSuccess: async (_result, { id }) => {
			await invalidateFormDefinitionRelated(queryClient);
			await queryClient.invalidateQueries({
				queryKey: formDefinitionKeys.detail(id),
			});
		},
	});
}
